import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { AccessibilityBar } from "@/components/AccessibilityBar";
import logo from "@/assets/logo.svg";
import logoDark from "@/assets/logo-dark.svg";

// In-page section anchors (only resolve on the home page) plus the Blog route.
const sectionLinks = [
  { label: "Services", hash: "#services" },
  { label: "How We Deliver", hash: "#deliver" },
  { label: "India", hash: "#india" },
  { label: "Global", hash: "#global" },
  { label: "About", hash: "#about" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  // Escape closes the mobile menu (expected dismiss behaviour for a disclosure)
  // and returns focus to the menu button, so a keyboard user is not dropped on
  // <body> when the control they were on disappears (WCAG 2.4.3).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      toggleRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // On the home page a bare "#services" jumps to the section; on any other route
  // it must be "/#services" so the browser navigates home first, then anchors.
  const sectionHref = (hash: string) => (onHome ? hash : `/${hash}`);
  const contactHref = onHome ? "#contact" : "/#contact";

  return (
    // <header> gives the site a `banner` landmark (implicit when not nested in
    // another sectioning element) so SR users can jump to it (WCAG 1.3.1 —
    // NCS-252 DF15). The fixed positioning lives here; the <nav> inside carries
    // the navigation landmark.
    <header className="fixed top-0 left-0 right-0 z-50 flex flex-col-reverse bg-background/90 backdrop-blur-md border-b shadow-sm">
      <nav aria-label="Main navigation">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-8 lg:px-16 h-16">
        <Link to="/" className="flex items-center rounded-sm focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring" aria-label="AccessBharat — Home">
          {/* Two lockups, swapped by theme (the dark one has light wordmark text). */}
          <img src={logo} alt="" width={320} height={72} className="h-[2.75rem] sm:h-[3.25rem] w-auto max-w-[42vw] sm:max-w-none object-contain object-left dark:hidden" aria-hidden="true" />
          <img src={logoDark} alt="" width={320} height={72} className="hidden h-[2.75rem] sm:h-[3.25rem] w-auto max-w-[42vw] sm:max-w-none object-contain object-left dark:block" aria-hidden="true" />
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {sectionLinks.map((l) => (
            <a key={l.hash} href={sectionHref(l.hash)} className="px-3 py-2 min-h-[44px] flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring">
              {l.label}
            </a>
          ))}
          <Link to="/blog" className="px-3 py-2 min-h-[44px] flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring" aria-current={pathname.startsWith("/blog") ? "page" : undefined}>
            Blog
          </Link>
          <Button variant="hero" size="sm" className="ml-3 min-h-[44px]" asChild>
            <a href={contactHref}>Schedule Audit</a>
          </Button>
        </div>

        <button
          ref={toggleRef}
          className="md:hidden p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-foreground focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring rounded-md"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls={open ? "mobile-menu" : undefined}
        >
          {open ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
        </button>
      </div>

      {open && (
        // Site navigation is a list of links, not an application menu — using
        // role="menu"/"menuitem" would promise arrow-key menu semantics that
        // aren't implemented (WCAG 4.1.2). Plain <ul>/<li> of links is correct.
        <ul id="mobile-menu" className="md:hidden bg-background border-b px-6 py-3 space-y-1 list-none shadow-md">
          {sectionLinks.map((l) => (
            <li key={l.hash}>
              <a href={sectionHref(l.hash)} onClick={() => setOpen(false)} className="flex items-center min-h-[44px] py-2 text-base text-muted-foreground hover:text-foreground focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring rounded-md">
                {l.label}
              </a>
            </li>
          ))}
          <li>
            <Link to="/blog" onClick={() => setOpen(false)} className="flex items-center min-h-[44px] py-2 text-base text-muted-foreground hover:text-foreground focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-focus-ring rounded-md" aria-current={pathname.startsWith("/blog") ? "page" : undefined}>
              Blog
            </Link>
          </li>
          <li className="py-2">
            <AccessibilityBar className="flex-wrap" />
          </li>
          <li>
            <Button variant="hero" size="sm" className="w-full mt-2 min-h-[44px]" asChild>
              <a href={contactHref} onClick={() => setOpen(false)}>Schedule Audit</a>
            </Button>
          </li>
        </ul>
      )}
      </nav>

      {/* Desktop: slim strip above the header row (mobile has the same controls
          in the menu). Placed after the nav in the DOM, shown first by flex-col-reverse. */}
      <div className="hidden border-b bg-muted/50 md:block">
        <div className="mx-auto flex h-11 max-w-7xl items-center justify-end px-8 lg:px-16">
          <AccessibilityBar />
        </div>
      </div>
    </header>
  );
};

export default Navbar;
